import { FC, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { DropdownItem, DropdownMenu, ExportIconImg, IconImg } from './DropDownExportPlannings.styled'

import UploadIcon from '@assets/images/upload-icon.svg'

import { ExportType, useExport } from '@utils/hooks'

import { PlanningReportInput } from '@types'

import { Button } from '@components/Button'
import { DropDown } from '@components/DropDown'

import { useSendPlanningObservations } from '@hooks/report/send-report-planning.hook'

import { PopUpExportPlanning } from '../PopUpExportPlanning'
import { useCalendarStore } from '../Calendar/Calendar.store'

type DropDownExportProps = {
  elementId: string
  fileName: string
  exportOptions: { id: ExportType | string; label: string; icon: string }[]
  projectId: string
}

export const DropDownExportPlannings: FC<DropDownExportProps> = ({ elementId, fileName, exportOptions, projectId }) => {
  const { t } = useTranslation()

  const { exportToPDF, exportAsImage } = useExport()

  const [searchParams] = useSearchParams()

  const [showExportPopUp, setShowExportPopUp] = useState<boolean>(false)

  const { sendPlanningReport } = useSendPlanningObservations()

  const visiblePeriod = useCalendarStore(({ state }) => state.visiblePeriod)

  const planningType = useMemo(() => {
    return Object.fromEntries(searchParams.entries()).type || 'Zoning'
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const handleExport = async (index: number) => {
    const element = document.getElementById(elementId)
    if (!element) return

    await waitForScrollToTop(element)

    if (index === 0) {
      exportToPDF(element, {
        fileName,
        planningType,
        orientation: 'portrait',
      })
    } else if (index === 1) {
      exportToPDF(element, {
        fileName,
        planningType,
        orientation: 'landscape',
      })
    } else {
      setShowExportPopUp(true)
    }
  }

  const waitForScrollToTop = async (element: HTMLElement) => {
    const top = 0

    return new Promise<void>((resolve) => {
      element.scrollTo({ top, behavior: 'smooth' })

      const checkIfScrollComplete = () =>
        element.scrollTop === top ? resolve() : requestAnimationFrame(checkIfScrollComplete)

      requestAnimationFrame(checkIfScrollComplete)
    })
  }

  return (
    <>
      <DropDown
        WrappedComponent={() => (
          <Button>
            <ExportIconImg src={UploadIcon} />
            {t('modules_project-settings_components_Planning_export')}
          </Button>
        )}
        WrappedChildren={({ hide }) => (
          <DropdownMenu
            onMouseMove={(event) => {
              event.stopPropagation()
            }}
          >
            {exportOptions.map(({ id, label, icon }, index) => (
              <DropdownItem
                key={`${id}-${index}`}
                onClick={() => {
                  handleExport(index)
                  hide()
                }}
              >
                <IconImg src={icon} alt={label} />
                <span>{label}</span>
              </DropdownItem>
            ))}
          </DropdownMenu>
        )}
      />
      {showExportPopUp && (
        <PopUpExportPlanning
          onSubmit={async (options) => {
            const element = document.getElementById(elementId)
            if (!element || !visiblePeriod) return

            setShowExportPopUp(false)

            setTimeout(() => {
              exportAsImage(element, { fileName }).then((localFile) => {
                const input: Omit<PlanningReportInput, 'planningFileId'> = {
                  ...options,
                  planningType,
                  projectId,
                  startDate: visiblePeriod.startDate,
                  endDate: visiblePeriod.endDate,
                }

                sendPlanningReport(input, localFile)
              })
            }, 100)
          }}
          onClose={() => {
            setShowExportPopUp(false)
          }}
        />
      )}
    </>
  )
}
